import {FileToUpload, FileUploadStatus} from "../models/fileToUpload";
import {apiSinglePartFileInitResponseData} from "../../../../models/apiS3FileData";
import WCCResourcedError from "../../../../models/wccResourcedError";
import {UploadToS3, UploadToS3Config, UploadToS3Result} from "./uploadToS3";
import {ILogsContainer} from "../../../../logsContainer";
import {UserLogsService} from "../../../../services/userLogs";
import {UploadCancelledError, UploadTimeoutError} from "../models/errorTypes";
import {SpeedoMeter} from "../../../../helpers/speedoMeter";

/**
 * The S3 uploader - It uploads a file to AWS S3 in a single go (without multipart/chunking). It does 2 things:
 * 1. Creates the associated user file in the db and creates a pre-signed link to upload to.
 * 2. Uploads the contents of the file at the generated pre-signed link.
 */
export class UploadToS3SinglePart extends UploadToS3 {
    /**
     * True if pausing and resuming the upload operation is supported; False otherwise
     * We must implement pauseUpload() and resumeUpload() functions if this is set to true
     */
    public override readonly supportsPauseAndResume: boolean = false;

    /**
     * The timeout for xhr request for each chunk upload
     * @private
     */
    private _xhrTimeout = 50000; //in milliseconds (or 50 seconds)
    
    /**
     * Initialises the S3 upload operation
     * @param file The file to upload
     * @param config The S3 upload operation config
     * @param logs The logs container
     * @param userLogsService The user logs service to send logs to the server
     */
    constructor(file: FileToUpload, config: UploadToS3Config, logs: ILogsContainer, userLogsService: UserLogsService) {
        super(file, config, logs, userLogsService);
    }

    /**
     * Triggers the S3 upload operation
     */
    override async upload(): Promise<void> {
        let userLogId = '';

        if (this._config.Events?.OnFileUploadStarting) {
            this._config.Events.OnFileUploadStarting();
        }

        this.changeCanBeDeleted(false);
        
        if (this.abortController == null) {
            this.abortController = new AbortController(); //initialize cancellation token controller
        }

        this._logs.addEntry("Starting the upload");
        
        //Create a log first
        try {
            const logCreationRequest = await this._userLogsService.createLogs(settings.discussionId);
            userLogId = logCreationRequest.UserLogId;
        }
        catch (error: any) {
            this.changeCanBeDeleted(true);
            system.showErrorMessage(messages.ThereWasASystemError);

            if (this._config.Events?.OnFileUploadComplete) {
                this._config.Events?.OnFileUploadComplete();
            }
            
            return;
        }
        
        /* ##################
                STEP 1
           #################
           
           Create the user file in the db
         */
        this.changeTextStatus(this._config.StaticStrings.CreatingFileMetaData);

        let initFileData: apiSinglePartFileInitResponseData;
        let initUploadRequest;
        
        try {
            this._logs.addEntry("Creating the user file record");
            
            initUploadRequest = await fetch(this._config.SingleFileStartUploadUrl,
                {
                    //Adding method type 
                    method: "POST",

                    //Adding body or contents to send 
                    body: JSON.stringify({
                        EndPoint: `https://s3-${this._config.Region}.amazonaws.com/${this._config.Bucket}`,
                        Folder: this._config.Folder,
                        Name: this.File.name(),
                        FileType: this.File.fileType,
                        FileSize: this.File.totalSize,
                        userLogId: userLogId
                    }),

                    //Adding headers to the request 
                    headers: {
                        "Content-type": "application/json",
                        "x-auth": this._config.AuthToken
                    },

                    signal: this.abortController.signal
                });

            if (initUploadRequest.ok) {
                this._logs.addEntry("User file created");
                
                //Get the user file data
                //Keep in mind that it is not yet uploaded, but here we get file id, signed url to upload to etc.
                initFileData = await initUploadRequest.json() as apiSinglePartFileInitResponseData;
                
                if (this._config.Events?.OnFileUploadStarted) {
                    this._config.Events.OnFileUploadStarted(initFileData.FileId);
                }
            } else {
                const responseText = await initUploadRequest.text();

                if (responseText.startsWith("{")) {
                    throw new WCCResourcedError(JSON.parse(responseText));
                }
                else {
                    this._logs.addEntry(responseText);
                    throw new Error(responseText);
                }
            }
        } catch (e: any) {
            let responseText;

            if (e instanceof WCCResourcedError) {
                const resourcedError = e as WCCResourcedError;
                responseText = resourcedError.toString();
                this.changeTextStatus(responseText);
            }
            else {
                responseText = (e as Error).message;
                this.changeTextStatus(responseText.substring(0, 50) + "....");
            }
            
            this.changeStatus(FileUploadStatus.Failed);
            
            if (this._config.Events?.OnFileUploadFailed && initUploadRequest != null) {
                this._config.Events.OnFileUploadFailed({ RequestStatus: initUploadRequest.status, ResponseText: responseText, StatusText: initUploadRequest.statusText });
            }
            
            if (system.isNotNullOrEmpty(userLogId)) {
                await this._userLogsService.updateLogs(userLogId, this._logs.GetAllCombinedLogs('\n'), {
                    result: enums.UserLogOperationResultTypes.Failed.value
                });
            }

            this.changeCanBeDeleted(true);

            if (this._config.Events?.OnFileUploadComplete) {
                this._config.Events?.OnFileUploadComplete();
            }
            
            return;
        }
        
        /* ##################
                STEP 2
           #################
           
           Upload the file to S3 now
         */

        this._logs.addEntry("Preparing the file to send to S3");
        
        this.changeTextStatus(this._config.StaticStrings.StartingUpload)

        //Prepare the request now
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', initFileData.SignedUploadUrl);
        
        let speedLogAdded = false;
        const speedMeter = new SpeedoMeter();
        
        //Hook-in the progress listener so that we can dispatch progress notifications
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                //Update progress
                this.changeUploadedSize(event.loaded);
                
                if (this.SpeedReportingProgressIntervals.includes(this.progress())) {
                    if (!speedLogAdded) {
                        this._logs.addEntry(`Upload ${this.progress()}% done with upload speed of ${speedMeter.getSpeedInMbps(event.loaded)}`);
                        speedLogAdded = true;
                    }
                }
                else {
                    speedLogAdded = false;
                }
                //console.log(`Upload ${this.progress()}% done with upload speed of ${speedMeter.getSpeedInMbps(event.loaded)}`);
            }
        });

        this.changeTextStatus(this._config.StaticStrings.Uploading)
        this.changeStatus(FileUploadStatus.Uploading);

        this._logs.addEntry("Ready to send the file to S3");
        
        //Start the uploading
        xhr.send(this.File.fileContents);

        this._logs.addEntry("Upload started");
        
        try {
            return await new Promise((resolve, reject) => {
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        //The request succeeded, change status and return result appropriately
                        this.changeStatus(FileUploadStatus.Uploaded);
                        this.changeTextStatus(this._config.StaticStrings.UploadedSuccessfully);
                        const result = <UploadToS3Result>{
                            FileId: initFileData.FileId,
                            UploadStatus: FileUploadStatus.Uploaded,
                            FileUrl: initFileData.SignedUrl
                        };
                        
                        this._logs.addEntry("File Uploaded Successfully");

                        if (system.isNotNullOrEmpty(userLogId)) {
                            this._userLogsService.updateLogs(userLogId, this._logs.GetAllCombinedLogs('\n'), {
                                result: enums.UserLogOperationResultTypes.Success.value
                            }).then(() => {
                                this.changeCanBeDeleted(true);

                                if (this._config.Events?.OnFileUploaded) {
                                    this._config.Events.OnFileUploaded(result);
                                }
                                
                                resolve();
                            });
                        }
                        else {
                            this.changeCanBeDeleted(true);

                            if (this._config.Events?.OnFileUploaded) {
                                this._config.Events.OnFileUploaded(result);
                            }

                            if (this._config.Events?.OnFileUploadComplete) {
                                this._config.Events?.OnFileUploadComplete();
                            }
                            
                            resolve();
                        }
                    } else {
                        const error = this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr);
                        this._logs.addEntry("File failed to upload. " + error);
                        reject(new Error(error));
                    }
                };

                xhr.onerror = () => {
                    const error = this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr);
                    this._logs.addEntry("File failed to upload. " + error);
                    reject(new Error(error));
                };

                xhr.ontimeout = () => {
                    this._logs.addEntry("File timed out..");
                    reject(new UploadTimeoutError(xhr.status == 0 ? this._config.StaticStrings.NetworkError : this._config.StaticStrings.TheRemoteEndpointReturnedAnError(xhr)));
                }

                //Handle cancellation
                this.abortController!.signal.addEventListener('abort', () => {
                    xhr.abort();
                    this.abortController = null;
                    reject(new UploadCancelledError(this._config.StaticStrings.UploadCancelled));
                });
            });
        }
        catch(e: any) {
            if (e instanceof UploadCancelledError) {
                const cancelledError = e as UploadCancelledError;

                //now reset everything
                this.changeStatus(FileUploadStatus.Cancelled);
                this.changeTextStatus(cancelledError.message);
            }
            else if (e instanceof UploadTimeoutError) {
                const timeoutError = e as UploadTimeoutError;

                //now reset everything
                this.changeStatus(FileUploadStatus.Failed);
                this.changeTextStatus(timeoutError.message);
            }
            else {
                this.changeStatus(FileUploadStatus.Failed);
                this.changeTextStatus(xhr.status == 0 ? this._config.StaticStrings.NetworkError : xhr.statusText);

                if (this._config.Events?.OnFileUploadFailed) {
                    this._config.Events.OnFileUploadFailed({
                        RequestStatus: xhr.status,
                        ResponseText: xhr.responseText,
                        StatusText: xhr.statusText
                    });
                }
            }

            this.changeTotalSize(0);
            this.changeUploadedSize(0);

            this.changeCanBeDeleted(true);
            
            if (system.isNotNullOrEmpty(userLogId)) {
                await this._userLogsService.updateLogs(userLogId, this._logs.GetAllCombinedLogs('\n'), {
                    result: this.status() == FileUploadStatus.Cancelled ? enums.UserLogOperationResultTypes.Cancelled.value : enums.UserLogOperationResultTypes.Failed.value
                });
            }

            if (this._config.Events?.OnFileUploadComplete) {
                this._config.Events?.OnFileUploadComplete();
            }
        }
    }
}